export type ComparisonOperator = '<' | '<=' | '>' | '>=' | '==' | '!=';

export type ConditionClause = {
  op: ComparisonOperator;
  field: string;
  value?: number | boolean | string;
  valueRef?: string;
  valueExpr?: string;
};

export type ConditionGroup = {
  allOf?: ConditionNode[];
  anyOf?: ConditionNode[];
  atLeastKOf?: {
    k: number;
    clauses: ConditionNode[];
  };
};

export type ConditionNode = ConditionClause | ConditionGroup;

export type AlertRule = {
  id: string;
  label: string;
  assetClasses: string[];
  severity: string;
  cooldownDays: number;
  conditions: ConditionNode;
  message: string;
};

export type ReadinessComponentRule = {
  name: string;
  max: number;
  scoreByAssetClass?: boolean;
  logic: Array<{
    when: ConditionClause;
    score?: number | string;
    scoreExpr?: string;
  }>;
};

export type ReadinessRule = {
  id: string;
  range: [number, number];
  labels: Array<{ min: number; max: number; label: string }>;
  components: ReadinessComponentRule[];
};

export type RegimeBadgeRule = {
  id: string;
  label: string;
  assetClasses: string[];
  conditions: ConditionNode;
  tooltip: string;
};

export type AssetClassConfig = {
  thresholds: Record<string, number>;
  readinessWeights: Record<string, number>;
};

export type CftcRules = {
  version: string;
  notes: string[];
  assetClasses: Record<string, AssetClassConfig>;
  computedFields: Record<string, string[]>;
  alerts: AlertRule[];
  readinessScore: ReadinessRule;
  regimeBadges: RegimeBadgeRule[];
};

export type AlertResult = {
  id: string;
  label: string;
  severity: string;
  message: string;
  cooldownDays: number;
  triggered: boolean;
  isSuppressed: boolean;
  lastFired?: number;
  timestamp?: number;
  mode?: "anchored" | "live";
};

export type ReadinessComponentScore = {
  name: string;
  score: number;
  max: number;
};

export type ReadinessResult = {
  score: number;
  label: string;
  components: ReadinessComponentScore[];
  mode?: "anchored" | "live";
};

export type ReadinessState = {
  triggerActive: boolean;
  pressureFreezeValue: number | null;
};

export type RegimeBadgeResult = {
  id: string;
  label: string;
  tooltip: string;
};

export type EvaluationResult = {
  alerts: AlertResult[];
  readiness: ReadinessResult;
  readinessState: ReadinessState;
  badge: RegimeBadgeResult | null;
  cooldownUpdates: Record<string, number>;
};

type EvaluationOptions = {
  now?: number;
  cooldownByAlert?: Record<string, number>;
  symbolKey?: string;
  readinessState?: ReadinessState;
};

const ABS_REGEX = /^abs\((.+)\)$/i;

const normalizeNumber = (value: unknown) => {
  if (value == null) {
    return null;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const resolveThreshold = (
  ref: string | undefined,
  thresholds: Record<string, number>
): number | null => {
  if (!ref) {
    return null;
  }
  const value = thresholds[ref];
  return Number.isFinite(value) ? value : null;
};

const resolveTokenValue = (
  token: string,
  context: Record<string, unknown>,
  thresholds: Record<string, number>,
  weights: Record<string, number>
) => {
  const trimmed = token.trim();
  if (!trimmed) {
    return null;
  }
  const absMatch = trimmed.match(ABS_REGEX);
  if (absMatch) {
    const inner = resolveTokenValue(absMatch[1], context, thresholds, weights);
    return inner == null ? null : Math.abs(inner);
  }
  const numeric = normalizeNumber(trimmed);
  if (numeric != null) {
    return numeric;
  }
  if (Object.prototype.hasOwnProperty.call(context, trimmed)) {
    return normalizeNumber(context[trimmed]);
  }
  if (Object.prototype.hasOwnProperty.call(thresholds, trimmed)) {
    return normalizeNumber(thresholds[trimmed]);
  }
  if (Object.prototype.hasOwnProperty.call(weights, trimmed)) {
    return normalizeNumber(weights[trimmed]);
  }
  return null;
};

export const evaluateExpression = (
  expr: string,
  context: Record<string, unknown>,
  thresholds: Record<string, number>,
  weights: Record<string, number>
) => {
  const parts = expr.split('*');
  if (!parts.length) {
    return null;
  }
  let result = 1;
  for (const part of parts) {
    const value = resolveTokenValue(part, context, thresholds, weights);
    if (value == null) {
      return null;
    }
    result *= value;
  }
  return result;
};

const resolveValue = (
  clause: ConditionClause,
  context: Record<string, unknown>,
  thresholds: Record<string, number>,
  weights: Record<string, number>
) => {
  if (clause.valueRef) {
    return resolveThreshold(clause.valueRef, thresholds);
  }
  if (clause.valueExpr) {
    return evaluateExpression(clause.valueExpr, context, thresholds, weights);
  }
  if (typeof clause.value === 'boolean') {
    return clause.value;
  }
  if (typeof clause.value === 'string') {
    const absMatch = clause.value.match(ABS_REGEX);
    if (absMatch) {
      const inner = resolveTokenValue(absMatch[1], context, thresholds, weights);
      return inner == null ? null : Math.abs(inner);
    }
  }
  return clause.value != null ? normalizeNumber(clause.value) : null;
};

const resolveFieldValue = (
  field: string,
  context: Record<string, unknown>,
  thresholds: Record<string, number>,
  weights: Record<string, number>
) => {
  const absMatch = field.match(ABS_REGEX);
  if (absMatch) {
    const inner = resolveFieldValue(absMatch[1], context, thresholds, weights);
    return inner == null ? null : Math.abs(inner);
  }
  if (Object.prototype.hasOwnProperty.call(context, field)) {
    const value = context[field];
    if (typeof value === 'boolean') {
      return value;
    }
    return normalizeNumber(value);
  }
  return null;
};

const compareValues = (left: unknown, right: unknown, op: ComparisonOperator) => {
  if (op === '==' || op === '!=') {
    const isEqual = left === right;
    return op === '==' ? isEqual : !isEqual;
  }
  const leftNum = normalizeNumber(left);
  const rightNum = normalizeNumber(right);
  if (leftNum == null || rightNum == null) {
    return false;
  }
  if (op === '<') {
    return leftNum < rightNum;
  }
  if (op === '<=') {
    return leftNum <= rightNum;
  }
  if (op === '>') {
    return leftNum > rightNum;
  }
  if (op === '>=') {
    return leftNum >= rightNum;
  }
  return false;
};

export const evaluateConditionNode = (
  node: ConditionNode,
  context: Record<string, unknown>,
  thresholds: Record<string, number>,
  weights: Record<string, number>
) => {
  const clause = node as ConditionClause;
  if (clause.op && clause.field) {
    const fieldValue = resolveFieldValue(clause.field, context, thresholds, weights);
    const targetValue = resolveValue(clause, context, thresholds, weights);
    return compareValues(fieldValue, targetValue, clause.op);
  }

  const group = node as ConditionGroup;
  const checks: boolean[] = [];
  if (group.allOf) {
    checks.push(group.allOf.every((item) => evaluateConditionNode(item, context, thresholds, weights)));
  }
  if (group.anyOf) {
    checks.push(group.anyOf.some((item) => evaluateConditionNode(item, context, thresholds, weights)));
  }
  if (group.atLeastKOf) {
    const { k, clauses } = group.atLeastKOf;
    const hits = clauses.filter((item) => evaluateConditionNode(item, context, thresholds, weights)).length;
    checks.push(hits >= k);
  }
  if (!checks.length) {
    return false;
  }
  return checks.every(Boolean);
};

const pickAssetClassConfig = (rules: CftcRules, assetClass: string) => {
  return rules.assetClasses[assetClass] || rules.assetClasses.FX;
};

const buildAlertKey = (symbolKey: string, alertId: string) => {
  return `${symbolKey}::${alertId}`;
};

export const evaluateRules = (
  context: Record<string, unknown> | null,
  rules: CftcRules,
  assetClass: string,
  options: EvaluationOptions = {}
): EvaluationResult => {
  const now = options.now ?? Date.now();
  const symbolKey = options.symbolKey || 'GLOBAL';
  const cooldownByAlert = options.cooldownByAlert || {};
  const normalizedAssetClass = rules.assetClasses[assetClass] ? assetClass : 'FX';
  const { thresholds, readinessWeights } = pickAssetClassConfig(rules, normalizedAssetClass);
  const safeContext = context || {};
  const cooldownUpdates: Record<string, number> = {};

  const alerts = rules.alerts
    .filter((alert) => alert.assetClasses.includes(normalizedAssetClass))
    .map((alert) => {
      const triggered = evaluateConditionNode(alert.conditions, safeContext, thresholds, readinessWeights);
      const key = buildAlertKey(symbolKey, alert.id);
      const lastFired = cooldownByAlert[key];
      const cooldownMs = alert.cooldownDays * 24 * 60 * 60 * 1000;
      const isSuppressed = Boolean(lastFired && now - lastFired < cooldownMs);
      if (triggered && !isSuppressed) {
        cooldownUpdates[key] = now;
      }
      return {
        id: alert.id,
        label: alert.label,
        severity: alert.severity,
        message: alert.message,
        cooldownDays: alert.cooldownDays,
        triggered,
        isSuppressed,
        lastFired,
        timestamp: triggered ? (isSuppressed ? lastFired : now) : undefined,
      };
    })
    .filter((alert) => alert.triggered && !alert.isSuppressed);

  const readinessComponents: ReadinessComponentScore[] = rules.readinessScore.components.map(
    (component) => {
      const componentMax = component.scoreByAssetClass
        ? readinessWeights[component.name] ?? component.max
        : component.max;
      let componentScore = 0;
      const shouldAccumulate =
        component.name === 'pressure' || component.name === 'trigger';
      // Pressure/Trigger stack multiple sub-signals; tiered components use first match.
      for (const rule of component.logic) {
        const matches = evaluateConditionNode(rule.when, safeContext, thresholds, readinessWeights);
        if (!matches) {
          continue;
        }
        let ruleScore: number | null = null;
        if (rule.scoreExpr) {
          const exprValue = evaluateExpression(
            rule.scoreExpr,
            safeContext,
            thresholds,
            readinessWeights
          );
          ruleScore = exprValue == null ? null : exprValue;
        } else if (rule.score != null) {
          if (typeof rule.score === 'string') {
            ruleScore = readinessWeights[rule.score] ?? componentMax;
          } else {
            ruleScore = rule.score;
          }
        } else {
          ruleScore = componentMax;
        }
        if (ruleScore == null) {
          continue;
        }
        if (shouldAccumulate) {
          componentScore += ruleScore;
        } else {
          componentScore = ruleScore;
          break;
        }
      }
      const normalizedScore = Math.max(0, Math.min(componentScore, componentMax));
      return {
        name: component.name,
        score: normalizedScore,
        max: componentMax,
      };
    }
  );

  const componentIndex = new Map(
    readinessComponents.map((component, index) => [component.name, index])
  );
  const offsidesIndex = componentIndex.get('offsides');
  const pressureIndex = componentIndex.get('pressure');
  const triggerIndex = componentIndex.get('trigger');
  const offsidesComponent =
    offsidesIndex != null ? readinessComponents[offsidesIndex] : null;
  const pressureComponent =
    pressureIndex != null ? readinessComponents[pressureIndex] : null;
  const triggerComponent =
    triggerIndex != null ? readinessComponents[triggerIndex] : null;

  if (offsidesComponent && triggerComponent) {
    const triggerCap = triggerComponent.max * 0.5;
    // Trigger strength is discounted when positioning is not offsides.
    if (offsidesComponent.score < 10 && triggerComponent.score > triggerCap) {
      triggerComponent.score = triggerCap;
    }
  }

  let triggerActive = triggerComponent ? triggerComponent.score >= 15 : false;
  let pressureFreezeValue =
    options.readinessState?.pressureFreezeValue ?? null;
  const wasTriggerActive = options.readinessState?.triggerActive ?? false;

  if (pressureComponent) {
    if (triggerActive) {
      // Pressure represents stress buildup; once price confirms, stress is releasing.
      if (!wasTriggerActive || pressureFreezeValue == null) {
        pressureFreezeValue = pressureComponent.score;
      }
      pressureComponent.score = pressureFreezeValue;
    } else {
      pressureFreezeValue = null;
    }
  }

  const rawScore = readinessComponents.reduce((sum, item) => sum + item.score, 0);
  const [minScore, maxScore] = rules.readinessScore.range;
  const score = Math.max(minScore, Math.min(rawScore, maxScore));
  const labelMatch = rules.readinessScore.labels.find(
    (range) => score >= range.min && score <= range.max
  );
  const readiness: ReadinessResult = {
    score,
    label: labelMatch ? labelMatch.label : '—',
    components: readinessComponents,
  };

  const badgePriority = ['squeeze_watch', 'flow_driven', 'macro_led'];
  let badge: RegimeBadgeResult | null = null;
  for (const badgeId of badgePriority) {
    const rule = rules.regimeBadges.find(
      (item) => item.id === badgeId && item.assetClasses.includes(normalizedAssetClass)
    );
    if (!rule) {
      continue;
    }
    const matches = evaluateConditionNode(rule.conditions, safeContext, thresholds, readinessWeights);
    if (matches) {
      badge = { id: rule.id, label: rule.label, tooltip: rule.tooltip };
      break;
    }
  }

  return {
    alerts,
    readiness,
    readinessState: {
      triggerActive,
      pressureFreezeValue,
    },
    badge,
    cooldownUpdates,
  };
};
